import { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, Power } from "lucide-react";
import { adminApi } from "@/lib/api";

export default function PopupSettings({ onSaved }) {
    const [data, setData] = useState({ title: "", content: "", active: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedFlash, setSavedFlash] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const popup = await adminApi.getPopup();
            setData(popup);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = useCallback(
        async (payload) => {
            setSaving(true);
            setError("");
            try {
                const updated = await adminApi.updatePopup(payload);
                setData(updated);
                setSavedFlash(true);
                setTimeout(() => setSavedFlash(false), 1800);
                if (onSaved) onSaved();
            } catch (e) {
                setError(e.message);
            } finally {
                setSaving(false);
            }
        },
        [onSaved]
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        save({
            title: data.title.trim(),
            content: data.content.trim(),
            active: data.active,
        });
    };

    const toggleActive = () => {
        const next = !data.active;
        save({ active: next });
    };

    if (loading) {
        return (
            <div className="py-20 flex items-center justify-center text-lpl-text-muted">
                <Loader2 size={20} className="animate-spin mr-2" />
                Chargement…
            </div>
        );
    }

    return (
        <div className="max-w-3xl" data-testid="popup-settings">
            <div className="mb-8">
                <p className="font-serif italic text-xl text-lpl-terracotta mb-2">
                    Annonce ponctuelle
                </p>
                <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[0.95] tracking-tight">
                    Pop-up du site
                </h2>
                <p className="mt-3 text-lpl-text-muted max-w-xl">
                    S'affiche sur le site public à l'arrivée de chaque visiteur,
                    une seule fois par session. Pratique pour annoncer une
                    fermeture, des vacances ou un évènement.
                </p>
            </div>

            {/* Toggle d'activation */}
            <div
                className="mb-6 flex items-center justify-between gap-4 p-5 border-2 border-lpl-border bg-lpl-bg-alt"
                data-testid="popup-active-toggle"
            >
                <div className="flex items-center gap-3">
                    <Power
                        size={18}
                        className={data.active ? "text-lpl-primary-deep" : "text-lpl-text-muted"}
                    />
                    <div>
                        <p className="font-display font-semibold text-base">
                            {data.active ? "Pop-up active" : "Pop-up désactivée"}
                        </p>
                        <p className="text-xs text-lpl-text-muted">
                            {data.active
                                ? "Visible sur le site public."
                                : "Masquée. Activez-la quand le contenu est prêt."}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={toggleActive}
                    disabled={saving}
                    role="switch"
                    aria-checked={data.active}
                    data-testid="popup-toggle-switch"
                    className={`relative inline-flex h-7 w-12 shrink-0 transition-colors ${
                        data.active ? "bg-lpl-primary" : "bg-lpl-border"
                    }`}
                >
                    <span
                        className={`block h-6 w-6 bg-lpl-bg shadow translate-y-0.5 transition-transform ${
                            data.active ? "translate-x-[22px]" : "translate-x-0.5"
                        }`}
                    />
                </button>
            </div>

            <form
                onSubmit={handleSubmit}
                className="bg-lpl-bg border border-lpl-border p-6 md:p-8 space-y-5"
                data-testid="popup-form"
            >
                <label className="block">
                    <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                        Titre
                    </span>
                    <input
                        type="text"
                        maxLength={200}
                        value={data.title}
                        onChange={(e) =>
                            setData((d) => ({ ...d, title: e.target.value }))
                        }
                        placeholder="Ex. Vacances d'été"
                        className="w-full px-3 py-2.5 bg-lpl-bg-alt border-2 border-lpl-border focus:border-lpl-text outline-none font-body text-sm text-lpl-text"
                        data-testid="popup-title-input"
                    />
                </label>

                <label className="block">
                    <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                        Contenu
                    </span>
                    <textarea
                        rows={6}
                        maxLength={2000}
                        value={data.content}
                        onChange={(e) =>
                            setData((d) => ({ ...d, content: e.target.value }))
                        }
                        placeholder="Ex. La boutique sera fermée du 1er au 15 août. À très vite !"
                        className="w-full px-3 py-2.5 bg-lpl-bg-alt border-2 border-lpl-border focus:border-lpl-text outline-none font-body text-sm text-lpl-text resize-y"
                        data-testid="popup-content-input"
                    />
                    <span className="block mt-1 text-[11px] text-lpl-text-muted">
                        {data.content.length} / 2000 caractères. Les sauts de
                        ligne sont conservés.
                    </span>
                </label>

                {error && (
                    <p
                        className="px-4 py-3 bg-lpl-terracotta/15 border-l-4 border-lpl-terracotta text-lpl-terracotta text-sm"
                        data-testid="popup-error"
                    >
                        {error}
                    </p>
                )}

                <div className="flex items-center justify-end gap-3 pt-2 border-t border-lpl-border">
                    {savedFlash && (
                        <span
                            className="text-sm text-lpl-primary-deep font-medium"
                            data-testid="popup-saved-flash"
                        >
                            ✓ Enregistré
                        </span>
                    )}
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-lpl-primary text-lpl-text text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors disabled:opacity-60"
                        data-testid="popup-save"
                    >
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </div>
            </form>

            {/* Aperçu */}
            <div className="mt-10">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-3 inline-flex items-center gap-2">
                    <Megaphone size={12} />
                    Aperçu sur le site
                </p>
                <div className="relative max-w-md bg-lpl-bg-alt p-6 border border-lpl-border">
                    <div
                        aria-hidden
                        className="absolute -top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-lpl-yellow/80 -rotate-[5deg]"
                    />
                    <p className="font-serif italic text-sm text-lpl-terracotta mb-1">
                        Une info importante
                    </p>
                    <p className="font-display font-semibold text-2xl leading-tight tracking-tight mb-2">
                        {data.title || (
                            <span className="text-lpl-text-muted italic">
                                (titre vide)
                            </span>
                        )}
                    </p>
                    <p className="text-sm text-lpl-text-muted whitespace-pre-line">
                        {data.content || (
                            <span className="italic">(contenu vide)</span>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
